/**
 * GEMS Admin — Payouts Queue
 *
 * Purpose: replace the spreadsheet-and-inbox loop with a single UI that
 * shows every pending withdrawal (oldest first), its FULL unmasked payout
 * destination, and lets the operator resolve it in one click:
 *   • Mark Paid   — stamps completed + external reference.
 *   • Mark Failed — atomically refunds the exact held rubies to the user.
 *
 * All money-critical logic (idempotent status transition, ruby restore) is
 * enforced server-side. This page never subtracts or adds balances on its
 * own — it only issues the two endpoint calls.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { RefreshCcw, CheckCircle2, XCircle, Loader2, Copy, User as UserIcon, Search } from 'lucide-react';

const STATUS_STYLES = {
  pending:    'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  paused:     'bg-purple-100 text-purple-800',
  completed:  'bg-emerald-100 text-emerald-700',
  failed:     'bg-rose-100 text-rose-700',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const fmtRubies = (n) => Number(n || 0).toLocaleString();
const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const copy = async (val) => {
  try {
    await navigator.clipboard.writeText(String(val ?? ''));
    toast.success('Copied');
  } catch {
    toast.error('Copy failed');
  }
};

const PayoutDetails = ({ method, details }) => {
  const rows = Object.entries(details || {}).filter(([, v]) => v !== '' && v != null);
  if (rows.length === 0) return <span className="text-gray-400 italic">(no details)</span>;
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-gray-500">{method}</div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 w-32 shrink-0">{k}</span>
          <span className="font-mono text-gray-800 truncate max-w-[260px]" title={String(v)}>{String(v)}</span>
          <button
            type="button"
            onClick={() => copy(v)}
            className="p-1 text-gray-400 hover:text-gray-700"
            title="Copy"
            data-testid={`payout-copy-${k}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

const MarkPaidModal = ({ row, onClose, onDone, api }) => {
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/withdrawals/${row.id}/mark-paid`, {
        external_reference: ref || null,
        note: note || null,
      });
      toast.success(data.already_processed ? 'Already marked paid' : 'Marked paid');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mark paid failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payout-mark-paid-modal">
      <div className="bg-white rounded-lg w-full max-w-md p-6 space-y-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Mark Paid
        </h3>
        <div className="text-sm text-gray-600">
          Paying <b>{fmtUsd(row.net_usd)}</b> to <b>{row.user_handle || row.user_email || row.user_id}</b>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">External reference</label>
          <input
            type="text"
            placeholder="e.g. PayPal batch id, Airwallex payout id"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            data-testid="payout-external-ref-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            data-testid="payout-note-input"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            data-testid="payout-mark-paid-cancel-btn"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            data-testid="payout-mark-paid-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Paid'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MarkFailedModal = ({ row, onClose, onDone, api }) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/withdrawals/${row.id}/mark-failed`, {
        reason: reason.trim(),
        note: note || null,
      });
      const refunded = data.refunded_rubies ?? 0;
      toast.success(
        data.already_processed
          ? 'Already marked failed'
          : `Marked failed — refunded ${refunded.toLocaleString()} rubies`
      );
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mark failed action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="payout-mark-failed-modal">
      <div className="bg-white rounded-lg w-full max-w-md p-6 space-y-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <XCircle className="w-5 h-5 text-rose-600" /> Mark Failed
        </h3>
        <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-800">
          This will refund <b>{fmtRubies(row.ruby_amount)} rubies</b> to
          {' '}<b>{row.user_handle || row.user_email || row.user_id}</b>.
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Reason (required)</label>
          <input
            type="text"
            placeholder="e.g. Bank rejected, invalid IBAN, customer requested cancel"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            data-testid="payout-failed-reason-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            data-testid="payout-failed-note-input"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            data-testid="payout-mark-failed-cancel-btn"
            disabled={busy}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="px-4 py-2 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50"
            data-testid="payout-mark-failed-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Failed & Refund'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PayoutsQueuePage = () => {
  const { api } = useAdmin();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [failTarget, setFailTarget] = useState(null);

  // Queue-scoped filters. `qInput` is what the user types; `q` is the
  // debounced value that actually hits the API. Status default matches
  // the backend default (pending/processing/paused).
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | all | completed | failed
  const debounceRef = useRef(null);

  // 350ms debounce so we don't hammer the queue endpoint per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [qInput]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append('q', q);
      if (statusFilter) params.append('status', statusFilter);
      const url = `/api/admin/withdrawals/queue${params.toString() ? '?' + params.toString() : ''}`;
      const { data } = await api.get(url);
      setRows(data.queue || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Reload when the debounced search term or status changes; also on mount.
  useEffect(() => { load({ silent: true }); }, [q, statusFilter]);

  const totals = useMemo(() => {
    const rubies = rows.reduce((s, r) => s + (r.ruby_amount || 0), 0);
    const usd = rows.reduce((s, r) => s + (r.net_usd || 0), 0);
    return { rubies, usd };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="payouts-queue-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payouts Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            Oldest-first list of pending, processing, and paused withdrawals.
            Payout destinations are shown unmasked so you can pay them out manually.
          </p>
        </div>
        <button
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded hover:bg-gray-50 disabled:opacity-50"
          data-testid="payouts-refresh-btn"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border rounded-lg p-4" data-testid="payouts-total-count">
          <div className="text-xs uppercase text-gray-500">Pending</div>
          <div className="text-2xl font-bold text-gray-900">{rows.length}</div>
        </div>
        <div className="bg-white border rounded-lg p-4" data-testid="payouts-total-rubies">
          <div className="text-xs uppercase text-gray-500">Held rubies</div>
          <div className="text-2xl font-bold text-rose-600">{fmtRubies(totals.rubies)}</div>
        </div>
        <div className="bg-white border rounded-lg p-4" data-testid="payouts-total-usd">
          <div className="text-xs uppercase text-gray-500">Net USD to pay</div>
          <div className="text-2xl font-bold text-emerald-600">{fmtUsd(totals.usd)}</div>
        </div>
      </div>

      {/* Queue-scoped search + status filter. This is intentionally NOT
          the global admin Search bar — it only affects the Payouts Queue. */}
      <div className="bg-white border rounded-lg p-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search payouts by @handle, name, email, or withdrawal id…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="payouts-queue-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="payouts-queue-status-filter"
        >
          <option value="">Pending (default)</option>
          <option value="all">All statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="processing">Processing</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {/* Queue table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading queue…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="payouts-empty-state">
            {q || statusFilter
              ? `No ${statusFilter === 'all' ? '' : (statusFilter || 'pending') + ' '}withdrawals match "${q}".`
              : '🎉 Queue is clear — no pending withdrawals.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-queue-table">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Requested</th>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-right px-4 py-3">Rubies</th>
                  <th className="text-right px-4 py-3">Net USD</th>
                  <th className="text-left px-4 py-3">Destination</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="align-top" data-testid={`payout-row-${r.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      <div>{fmtDate(r.requested_at)}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium">
                        <UserIcon className="w-4 h-4 text-gray-400" />
                        {r.user_handle ? `@${r.user_handle}` : r.user_name || '—'}
                      </div>
                      <div className="text-xs text-gray-500">{r.user_email || r.user_id}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-rose-600">{fmtRubies(r.ruby_amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-emerald-700">{fmtUsd(r.net_usd)}</div>
                      <div className="text-xs text-gray-400">
                        gross {fmtUsd(r.gross_usd)} · fees {fmtUsd((r.platform_fee_usd || 0) + (r.processing_fee_usd || 0))}
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <PayoutDetails method={r.payment_method} details={r.payout_details} />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-700'}`}
                        data-testid={`payout-status-${r.id}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => setPayTarget(r)}
                          className="px-2.5 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700"
                          data-testid={`payout-mark-paid-btn-${r.id}`}
                        >
                          Mark Paid
                        </button>
                        <button
                          onClick={() => setFailTarget(r)}
                          className="px-2.5 py-1.5 text-xs font-medium bg-rose-600 text-white rounded hover:bg-rose-700"
                          data-testid={`payout-mark-failed-btn-${r.id}`}
                        >
                          Mark Failed
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {payTarget && (
        <MarkPaidModal
          row={payTarget}
          api={api}
          onClose={() => setPayTarget(null)}
          onDone={() => { setPayTarget(null); load({ silent: true }); }}
        />
      )}
      {failTarget && (
        <MarkFailedModal
          row={failTarget}
          api={api}
          onClose={() => setFailTarget(null)}
          onDone={() => { setFailTarget(null); load({ silent: true }); }}
        />
      )}
    </div>
  );
};

export default PayoutsQueuePage;
